import {useEffect} from 'react';
import {useMutation, useQuery} from '@tanstack/react-query';
import {apiClient, queryClient} from '@common/http/query-client';
import {showHttpErrorToast} from '@common/http/show-http-error-toast';
import {message} from '@ui/i18n/message';
import {CssTheme} from '@ui/themes/css-theme';
import {
  getBootstrapData,
  mergeBootstrapData,
} from '@ui/bootstrap-data/bootstrap-data-store';
import {setCookie} from '@ui/utils/hooks/use-cookie';
import {applyThemeToDom} from '@ui/themes/utils/apply-theme-to-dom';
import {toast} from '@ui/toast/toast';

const storageKey = 'be-active-theme';
export const userThemesQueryKey = ['users', 'me', 'theme'];

export interface UserThemeInfo extends CssTheme {
  allowed_price_labels?: string[];
}

export interface UserThemesResponse {
  themes: UserThemeInfo[];
  selectedThemeId: number | null;
}

interface RefreshUserThemeStateOptions {
  preserveCurrentSelectedThemeWhenNoUserTheme?: boolean;
}

interface UseApplyUserThemeOptions {
  onSuccess?: () => void;
  showSuccessToast?: boolean;
}

export function useUserThemes(options: {enabled?: boolean} = {}) {
  const query = useQuery({
    queryKey: userThemesQueryKey,
    queryFn: fetchUserThemes,
    enabled: options.enabled,
  });

  useEffect(() => {
    if (!query.data) return;

    syncBootstrapWithUserThemes(query.data, {
      preserveCurrentSelectedThemeWhenNoUserTheme: true,
    });
  }, [query.data]);

  return {
    ...query,
    themes: query.data?.themes ?? [],
    selectedThemeId: query.data?.selectedThemeId ?? null,
  };
}

export function useApplyUserTheme(options: UseApplyUserThemeOptions = {}) {
  return useMutation({
    mutationFn: (themeId: number | null) => persistUserTheme(themeId),
    onSuccess: (_response, themeId) => {
      if (options.showSuccessToast !== false) {
        toast.positive(
          themeId === null
            ? message('Site default theme applied successfully.')
            : message('Theme applied successfully.'),
        );
      }

      options.onSuccess?.();
    },
    onError: error => {
      if ((error as any)?.response?.status === 403) {
        showHttpErrorToast(
          error,
          message('This theme is not available on your plan.'),
        );
      } else {
        showHttpErrorToast(
          error,
          message('Could not apply selected theme.'),
        );
      }
    },
  });
}

export async function refreshAppliedUserThemeState(
  options: RefreshUserThemeStateOptions = {},
): Promise<UserThemesResponse> {
  const data = await fetchUserThemes();

  syncBootstrapWithUserThemes(data, options);
  queryClient.setQueryData(userThemesQueryKey, data);
  dispatchUserThemeReload();

  return data;
}

async function persistUserTheme(themeId: number | null) {
  await apiClient.put('users/me/theme', {theme_id: themeId});

  setCookie(storageKey, themeId != null ? `${themeId}` : '');

  return refreshAppliedUserThemeState({
    preserveCurrentSelectedThemeWhenNoUserTheme: false,
  });
}

async function fetchUserThemes(): Promise<UserThemesResponse> {
  const response = await apiClient.get<UserThemesResponse>('users/me/theme');

  return {
    themes: (response.data?.themes ?? []).map(normalizeTheme),
    selectedThemeId: response.data?.selectedThemeId ?? null,
  };
}

function syncBootstrapWithUserThemes(
  data: UserThemesResponse,
  options: RefreshUserThemeStateOptions,
) {
  const currentData = getBootstrapData();
  if (!currentData?.themes) return;

  const mergedThemes = mergeThemes(currentData.themes.all ?? [], data.themes);
  const nextSelectedThemeId = resolveSelectedThemeId(
    mergedThemes,
    data.selectedThemeId,
    currentData.themes.selectedThemeId,
    options,
  );

  mergeBootstrapData({
    user: currentData.user
      ? {...currentData.user, theme_id: data.selectedThemeId ?? null}
      : currentData.user,
    themes: {
      ...currentData.themes,
      all: mergedThemes,
      selectedThemeId: nextSelectedThemeId,
    },
  });

  const selectedTheme = mergedThemes.find(
    theme => `${theme.id}` === `${nextSelectedThemeId}`,
  );

  if (selectedTheme) {
    applyThemeToDom(selectedTheme);
  }
}

function mergeThemes(
  currentThemes: CssTheme[],
  fetchedThemes: UserThemeInfo[],
): UserThemeInfo[] {
  const defaults = currentThemes
    .filter(theme => theme.default_dark || theme.default_light)
    .map(normalizeTheme);
  const fetched = fetchedThemes.map(normalizeTheme);
  const themes = new Map<string, UserThemeInfo>();

  defaults.forEach(theme => {
    themes.set(`${theme.id}`, theme);
  });

  fetched.forEach(theme => {
    const existing = themes.get(`${theme.id}`);
    themes.set(`${theme.id}`, {...existing, ...theme});
  });

  return Array.from(themes.values()).sort((first, second) => {
    const firstIsDefault = !!(first.default_dark || first.default_light);
    const secondIsDefault = !!(second.default_dark || second.default_light);

    if (firstIsDefault !== secondIsDefault) {
      return firstIsDefault ? -1 : 1;
    }

    return `${first.id}`.localeCompare(`${second.id}`, undefined, {
      numeric: true,
    });
  });
}

function resolveSelectedThemeId(
  themes: UserThemeInfo[],
  selectedThemeId: number | null,
  currentSelectedThemeId: number | string | null | undefined,
  options: RefreshUserThemeStateOptions,
) {
  if (
    selectedThemeId != null &&
    themes.some(theme => `${theme.id}` === `${selectedThemeId}`)
  ) {
    return selectedThemeId;
  }

  if (
    options.preserveCurrentSelectedThemeWhenNoUserTheme &&
    currentSelectedThemeId != null &&
    themes.some(theme => `${theme.id}` === `${currentSelectedThemeId}`)
  ) {
    return currentSelectedThemeId;
  }

  return (
    themes.find(theme => theme.default_light)?.id ??
    themes.find(theme => theme.default_dark)?.id ??
    themes[0]?.id ??
    null
  );
}

function normalizeTheme(theme: Partial<UserThemeInfo>): UserThemeInfo {
  return {
    ...theme,
    id: theme.id!,
    name: theme.name || `Theme #${theme.id}`,
    values: theme.values ?? {},
  };
}

function dispatchUserThemeReload() {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(new Event('user-theme:reload'));
}
